import {Alert, Card, Collapse, Input as AntInput} from 'antd';
import {CreateSubscriptionInput, Interval, useStoresQuery} from 'api/generated';
import {BottomActions, CancelButton, PhoneNumberInput, Spacer} from 'components';
import {useFormikContext} from 'formik';
import {Form, FormItem, Input, InputNumber, Select, SubmitButton} from 'formik-antd';
import {useCountries} from 'hooks/useCountries';
import {useDefaultCurrency} from 'hooks/useDefaultCurrency';
import React from 'react';
import styled from 'styled-components';
import {ScrollToError} from '../../../components/Form/ScrollToError';
import {useAccessLevel} from '../../../hooks/useAccessLevel';
import {ContactDetailsFields} from '../PayByLink/ContactDetailsFields';

export type FormValues = CreateSubscriptionInput;

type Props = {
  isNew: boolean;
  accountId: string;
};

const Panel = styled(Collapse.Panel)`
  margin: 0 -16px;
`;

const Group = styled(AntInput.Group)`
  display: flex !important;
  width: 100%;
  align-items: flex-end;
  & > * {
    flex: 1;
  }
`;

export const EditSubscriptionForm: React.FC<Props> = ({isNew, accountId}) => {
  const {currency} = useDefaultCurrency();
  const {currentCountry} = useCountries();
  const {values} = useFormikContext<FormValues>();
  const {isAdmin} = useAccessLevel();

  const {data: storesData} = useStoresQuery({skip: !isAdmin, variables: {accountId}});
  const stores = storesData?.stores.items;

  const intervalOptions = [
    {label: values.intervalCount === 1 ? 'day' : 'days', value: Interval.DAY},
    {label: values.intervalCount === 1 ? 'week' : 'weeks', value: Interval.WEEK},
    {label: values.intervalCount === 1 ? 'month' : 'months', value: Interval.MONTH},
    {label: values.intervalCount === 1 ? 'year' : 'years', value: Interval.YEAR}
  ];

  return (
    <Form layout="vertical" preserve={false}>
      {!isNew && (
        <Alert
          message={<>Update will only take effect at the end of the current billing cycle.</>}
          style={{marginBottom: 20}}
        />
      )}
      <Card>
        <FormItem name="amount" label={'Amount'}>
          <InputNumber
            type="number"
            name="amount"
            style={{width: '100%'}}
            precision={2}
            step={0.01}
            addonAfter={currency}
            onChange={console.log}
          />
        </FormItem>
        <Group compact>
          <FormItem name="intervalCount" label="Billing period">
            <Input type="number" name="intervalCount" min={1} />
          </FormItem>
          <FormItem name="interval">
            <Select name="interval" placeholder="Select interval">
              {intervalOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        </Group>
        {isNew && (
          <FormItem name="trialPeriodDays" label="Trial period days">
            <Input type="number" name="trialPeriodDays" min={0} />
          </FormItem>
        )}
        <FormItem name="description" label="Description">
          <Input name="description" type="textarea" placeholder="Enter a description" />
        </FormItem>
        <FormItem name="customer.name" label="Customer name">
          <Input name="customer.name" placeholder="John Doe" />
        </FormItem>
        <FormItem name="customer.email" label="Customer email">
          <Input name="customer.email" placeholder="email@example.com" />
        </FormItem>
        <FormItem name="customer.phone" label="Customer phone">
          <PhoneNumberInput
            name="customer.phone"
            allowClear
            allowEmpty
            countryCode={currentCountry?.code ?? 'ES'}
          />
        </FormItem>
        {isNew && stores && Boolean(stores.length) && (
          <FormItem name="storeId" label="Store">
            <Select name="storeId" allowClear placeholder="Select a store">
              {stores.map((store) => (
                <Select.Option key={store.id} value={store.id}>
                  {store.name}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        )}
        <Collapse ghost>
          <Panel header="Billing Address" key="billingDetails">
            <ContactDetailsFields name="billingDetails" />
          </Panel>
        </Collapse>

        <Collapse ghost>
          <Panel header="Shipping Address" key="shippingDetails">
            <ContactDetailsFields name="shippingDetails" />
          </Panel>
        </Collapse>
        <input type="submit" value="submit" hidden />
        <ScrollToError />
      </Card>
      <BottomActions>
        <CancelButton backPath={isNew ? `/accounts/${accountId}?tab=subscriptions` : undefined}>
          Cancel
        </CancelButton>
        <Spacer />
        <SubmitButton>{isNew ? 'Create subscription' : 'Update subscription'}</SubmitButton>
      </BottomActions>
    </Form>
  );
};
